import Pjax from "pjax"
import { ClientPage } from "../../submodules/rapdv/client/elements/PagesCtrl"
import MediumEditor from "medium-editor/dist/js/medium-editor.min.js"
import { Modal } from "../../submodules/rapdv/client/elements/Bootstrap"
import { Default } from "../Default"
import { Currency } from "../../submodules/rapdv/server/types/Currency"
import { Money } from "../../submodules/rapdv/server/types/Money"
import { SocketClient } from "../socket/SocketClient"
import type { MenuGenerationStatus } from "../../server/socket/SocketServer"
import { SocketEvents } from "../../server/socket/SocketEvents"
import { DishFeeType } from "../elements/Menu"
import { Translation } from "../elements/Translation"

export class PanelMenuEditor implements ClientPage {

  private static readonly TRANSLATION_ID = "MenuEditorPanel"
  
  private static readonly MENU_MODE_PHOTOS = "photos"
  private static readonly MENU_MODE_MANUAL = "manual"
  private static readonly CSS_HAS_FILES = "has-files"
  private static readonly CSS_DRAG_OVER = "drag-over"
  private static readonly CSS_UNAVAILABLE = "is-unavailable"
  private static readonly CSS_HALF_AND_HALF = "can-order-half-and-half"
  private static readonly PRICE_DECIMAL_PLACES = 2
  private static readonly DEFAULT_PRICE = "0"
  private static readonly MIN_SIZES_FOR_FEE_SELECTION = 2
  private static readonly MS_IN_SECOND = 1000

  private categoryCounter = 0
  private foodCounter = 0
  private sizeCounter = 0
  private feeCounter = 0
  private currencyCode = ""
  private languageCode = ""
  private restaurantKey = ""
  private selectedFiles: File[] = []
  private currentMode = PanelMenuEditor.MENU_MODE_PHOTOS
  private wasMenuCreated = false
  private isCreatingMenu = false
  private pendingDeleteEl: HTMLElement | null = null
  private deleteModal: Modal | null = null
  private feeModal: Modal | null = null
  private pendingFeeParentEl: HTMLElement | null = null
  private pendingFeeEditEl: HTMLElement | null = null
  private addonsModal: Modal | null = null
  private pendingAddonsEl: HTMLElement | null = null
  private createBtnOriginalHTML = ""
  private pjax: Pjax
  private ocrCountdownTimer: number | null = null
  private translation = new Translation()
  private t = (key: string): string => this.translation.t(key)

  getPageId = (): string => "menu-editor-panel"

  execute = async (pjax: Pjax): Promise<void> => {
    this.pjax = pjax
    await this.translation.fetchTranslations(PanelMenuEditor.TRANSLATION_ID)

    const manualPanel = document.querySelector<HTMLElement>('[data-menu-panel="manual"]')
    this.currencyCode = manualPanel?.dataset.currencyCode ?? ""
    this.languageCode = manualPanel?.dataset.languageCode ?? ""
    this.restaurantKey = manualPanel?.dataset.restaurantKey ?? ""
    this.wasMenuCreated = manualPanel?.dataset.wasMenuCreated === "true"
    this.setupModeToggle()
    this.setupFormSubmit()
    this.setupDropzone()
    this.setupCreateBtn()
    this.setupDeleteModal()
    this.setupFeeModal()
    this.setupAddonsModal()
    this.setupAddCategory()
    this.preRenderMenu()
    this.initOcrState()
    this.setupRichText("introduction")
    this.setupRichText("endnotes")
    SocketClient.on(SocketEvents.MenuGenerationStatus, this.onMenuGenerationStatus)
  }

  private updateHeaderText = (): void => {
    const mode = this.currentMode
    document.querySelectorAll<HTMLElement>("[data-menu-title]").forEach((el) => {
      el.classList.toggle("d-none", el.dataset.menuTitle !== mode)
    })
    document.querySelectorAll<HTMLElement>("[data-menu-subtitle]").forEach((el) => {
      el.classList.toggle("d-none", el.dataset.menuSubtitle !== mode)
    })
    document.querySelectorAll<HTMLElement>("[data-menu-toggle-label]").forEach((el) => {
      el.classList.toggle("d-none", el.dataset.menuToggleLabel !== mode)
    })
  }

  private setupModeToggle = (): void => {
    const toggleBtn = document.querySelector<HTMLElement>("[data-menu-mode-toggle]")
    if (!toggleBtn) return
    toggleBtn.addEventListener("click", () => {
      this.currentMode = this.currentMode === PanelMenuEditor.MENU_MODE_PHOTOS ? PanelMenuEditor.MENU_MODE_MANUAL : PanelMenuEditor.MENU_MODE_PHOTOS
      document.querySelectorAll<HTMLElement>("[data-menu-panel]").forEach((panel) => {
        panel.style.display = panel.dataset.menuPanel === this.currentMode ? "" : "none"
      })
      this.updateHeaderText()
    })
  }

  private onFilesSelected = (
    files: FileList | null,
    createBtn: HTMLButtonElement,
    content: HTMLElement | null,
    filesView: HTMLElement | null,
    filesLabel: HTMLElement | null,
    dropzone: HTMLElement
  ): void => {
    if (!files || files.length === 0) return
    const imageFiles = Array.from(files).filter((file) => file.type.startsWith("image/"))
    if (imageFiles.length === 0) return
    this.selectedFiles = imageFiles
    createBtn.disabled = false
    content?.classList.add("d-none")
    filesView?.classList.remove("d-none")
    if (filesLabel) filesLabel.textContent = `${imageFiles.length} ${imageFiles.length > 1 ? this.t("files selected") : this.t("file selected")}`
    dropzone.classList.add(PanelMenuEditor.CSS_HAS_FILES)
  }

  private setDropzoneLoading = (loading: boolean): void => {
    const dropzone = document.querySelector<HTMLElement>("[data-menu-dropzone]")
    if (!dropzone) return
    const input = dropzone.querySelector<HTMLInputElement>(".menu-dropzone-input")
    const content = dropzone.querySelector<HTMLElement>(".menu-dropzone-content")
    const filesView = dropzone.querySelector<HTMLElement>("[data-menu-dropzone-files]")
    const loadingEl = dropzone.querySelector<HTMLElement>("[data-menu-dropzone-loading]")

    if (loading) {
      if (input) input.disabled = true
      content?.classList.add("d-none")
      filesView?.classList.add("d-none")
      loadingEl?.classList.remove("d-none")
    } else {
      if (input) input.disabled = false
      loadingEl?.classList.add("d-none")
      const hasFiles = this.selectedFiles.length > 0
      content?.classList.toggle("d-none", hasFiles)
      filesView?.classList.toggle("d-none", !hasFiles)
      if (!hasFiles) dropzone.classList.remove(PanelMenuEditor.CSS_HAS_FILES)
    }
  }

  private setupDropzone = (): void => {
    const dropzone = document.querySelector<HTMLElement>("[data-menu-dropzone]")
    const input = dropzone?.querySelector<HTMLInputElement>(".menu-dropzone-input")
    const createBtn = document.querySelector<HTMLButtonElement>("[data-menu-create-btn]")
    const content = dropzone?.querySelector<HTMLElement>(".menu-dropzone-content")
    const filesView = dropzone?.querySelector<HTMLElement>("[data-menu-dropzone-files]")
    const filesLabel = dropzone?.querySelector<HTMLElement>("[data-menu-files-label]")

    if (!dropzone || !input || !createBtn) return

    input.addEventListener("change", () => this.onFilesSelected(input.files, createBtn, content, filesView, filesLabel, dropzone))

    dropzone.addEventListener("dragover", (event) => { if (this.isCreatingMenu) return; event.preventDefault(); dropzone.classList.add(PanelMenuEditor.CSS_DRAG_OVER) })
    dropzone.addEventListener("dragleave", () => dropzone.classList.remove(PanelMenuEditor.CSS_DRAG_OVER))
    dropzone.addEventListener("drop", (event) => {
      event.preventDefault()
      dropzone.classList.remove(PanelMenuEditor.CSS_DRAG_OVER)
      if (this.isCreatingMenu) return
      this.onFilesSelected(event.dataTransfer?.files ?? null, createBtn, content, filesView, filesLabel, dropzone)
    })
  }

  private setupCreateBtn = (): void => {
    const createBtn = document.querySelector<HTMLButtonElement>("[data-menu-create-btn]")
    const errorEl = document.querySelector<HTMLElement>("[data-menu-create-error]")
    if (!createBtn) return

    this.createBtnOriginalHTML = createBtn.innerHTML

    createBtn.addEventListener("click", async () => {
      if (this.isCreatingMenu || this.selectedFiles.length === 0) return
      this.isCreatingMenu = true
      this.setDropzoneLoading(true)

      const toggleBtn = document.querySelector<HTMLButtonElement>("[data-menu-mode-toggle]")
      const originalInnerHTML = createBtn.innerHTML
      createBtn.disabled = true
      if (toggleBtn) toggleBtn.disabled = true
      createBtn.innerHTML = `<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>${this.t("Processing...")}`
      errorEl?.classList.add("d-none")

      const formData = new FormData()
      for (const file of this.selectedFiles) {
        formData.append("files", file)
      }
      const csrfToken = document.querySelector<HTMLInputElement>('input[name="_csrf"]')?.value
      if (csrfToken) formData.append("_csrf", csrfToken)

      try {
        const response = await fetch(`/api/v1/menu/generate/${this.restaurantKey}`, {
          method: "POST",
          body: formData,
        })

        if (response.ok) return

        const data = await response.json().catch(() => ({}))
        if (errorEl) {
          errorEl.textContent = data.error ?? this.t("Failed to generate menu")
          errorEl.classList.remove("d-none")
        }
      } catch {
        if (errorEl) {
          errorEl.textContent = this.t("Failed to generate menu. Please try again.")
          errorEl.classList.remove("d-none")
        }
      }

      this.isCreatingMenu = false
      this.setDropzoneLoading(false)
      createBtn.disabled = false
      createBtn.innerHTML = originalInnerHTML
      if (toggleBtn) toggleBtn.disabled = false
    })
  }

  private setupRichText = (noteKey: string): void => {
    const noteBody = document.querySelector(`[data-menu-note="${noteKey}"]`) as HTMLElement
    const richText = noteBody?.querySelector<HTMLElement>("[data-menu-field]")
    if (!richText) return
    new MediumEditor(richText, { ...Default.MEDIUM_EDITOR_OPTIONS, placeholder: { text: this.t("Type your text") } })
  }

  private setupDeleteModal = (): void => {
    const modalEl = document.getElementById("menu-delete-modal")
    if (!modalEl) return
    this.deleteModal = new Modal(modalEl)
    document.getElementById("menu-delete-confirm-btn")?.addEventListener("click", () => {
      this.pendingDeleteEl?.remove()
      this.pendingDeleteEl = null
      this.deleteModal?.hide()
    })
  }

  private confirmDelete = (targetEl: HTMLElement): void => {
    this.pendingDeleteEl = targetEl
    this.deleteModal?.show()
  }

  private setupAddCategory = (): void => {
    const addCategoryBtn = document.querySelector('[data-menu-action="add-category"]')
    addCategoryBtn?.addEventListener("click", () => this.addCategory())
  }

  private addCategory = (initialData?: { name: string; description: string; enabled?: boolean; halfAndHalf?: boolean; addons?: any[]; fees: any[]; dishes: any[] }): void => {
    const container = document.getElementById("menu-categories")
    if (!container) return

    const categoryId = ++this.categoryCounter
    const categoryEl = document.createElement("div")
    categoryEl.className = "menu-category"
    categoryEl.dataset.categoryId = String(categoryId)
    categoryEl.innerHTML = this.buildCategoryHtml(categoryId)
    container.appendChild(categoryEl)

    this.setupCategory(categoryEl)

    if (initialData) {
      const titleEl = categoryEl.querySelector<HTMLElement>(".menu-category-title")
      if (titleEl) titleEl.textContent = initialData.name

      if (initialData.description) {
        const descriptionEl = categoryEl.querySelector<HTMLElement>(".menu-category-description")
        const addDescriptionBtn = categoryEl.querySelector<HTMLElement>('[data-menu-action="add-category-description"]')
        descriptionEl?.classList.remove("d-none")
        if (descriptionEl) descriptionEl.textContent = initialData.description
        addDescriptionBtn?.classList.add("d-none")
      }

      const feesContainer = categoryEl.querySelector<HTMLElement>("[data-fees-container]")
      for (const fee of initialData.fees ?? []) {
        if (feesContainer) this.addFeePill(feesContainer, fee.name, fee.type, fee.price, fee.percentage, fee.deliveryMethods ?? [], [])
      }

      for (const dish of initialData.dishes ?? []) {
        this.addFoodItem(categoryEl, String(categoryId), dish)
      }

      if (initialData.halfAndHalf === true) {
        const toggleBtn = categoryEl.querySelector<HTMLElement>('[data-menu-action="toggle-half-and-half"]')
        this.markHalfAndHalf(categoryEl, categoryEl.querySelector<HTMLElement>(".menu-category-fees"), toggleBtn)
      }

      if (Array.isArray(initialData.addons) && initialData.addons.length > 0) {
        this.applyAddonsToElement(categoryEl, initialData.addons)
      }

      if (initialData.enabled === false) {
        const markBtn = categoryEl.querySelector<HTMLElement>('[data-menu-action="mark-category-unavailable"]')
        this.markUnavailable(categoryEl, categoryEl.querySelector<HTMLElement>(".menu-category-fees"), markBtn)
      }
    } else {
      const title = categoryEl.querySelector<HTMLElement>(".menu-category-title")
      title?.focus()
    }
  }

  private buildCategoryHtml = (categoryId: number): string => `
    <div class="menu-category-row">
      <div class="menu-category-title" contenteditable="true" data-placeholder="${this.t("Category Title")}"></div>
      <div class="dropdown">
        <button type="button" class="btn btn-sm btn-outline-secondary" data-bs-toggle="dropdown" aria-expanded="false">+</button>
        <ul class="dropdown-menu dropdown-menu-end">
          <li><button class="dropdown-item" data-menu-action="add-category-description">${this.t("Description")}</button></li>
          <li><button class="dropdown-item" data-menu-action="add-category-fee">${this.t("Fee")}</button></li>
          <li><button class="dropdown-item" data-menu-action="edit-addons">${this.t("Add-ons")}</button></li>
          <li><button class="dropdown-item" data-menu-action="toggle-half-and-half">${this.t("Allow ordering half and half")}</button></li>
          <li><button class="dropdown-item" data-menu-action="mark-category-unavailable">${this.t("Mark unavailable")}</button></li>
          <li><hr class="dropdown-divider" /></li>
          <li><button class="dropdown-item text-danger" data-menu-action="delete-category">${this.t("Delete")}</button></li>
        </ul>
      </div>
    </div>
    <div class="menu-category-description d-none" contenteditable="true" data-placeholder="${this.t("Add a description…")}"></div>
    <div class="menu-category-fees" data-fees-container></div>
    <div class="menu-food-items" data-food-container="${categoryId}"></div>
    <div class="btn btn-sm btn-outline-secondary" data-menu-action="add-food">${this.t("+ Add Item")}</div>
  `

  private setupCategory = (categoryEl: HTMLElement): void => {
    const categoryId = categoryEl.dataset.categoryId!

    const title = categoryEl.querySelector<HTMLElement>(".menu-category-title")!
    const description = categoryEl.querySelector<HTMLElement>(".menu-category-description")!
    const addFoodBtn = categoryEl.querySelector<HTMLElement>('[data-menu-action="add-food"]')!

    title.addEventListener("keydown", (event) => {
      if (event.key === "Enter") { event.preventDefault(); title.blur() }
    })

    const addDescriptionBtn = categoryEl.querySelector<HTMLElement>('[data-menu-action="add-category-description"]')
    if (!description.classList.contains("d-none")) addDescriptionBtn?.classList.add("d-none")
    addDescriptionBtn?.addEventListener("click", () => {
      description.classList.remove("d-none")
      description.focus()
      addDescriptionBtn.classList.add("d-none")
    })

    categoryEl.querySelector('[data-menu-action="delete-category"]')?.addEventListener("click", () => {
      this.confirmDelete(categoryEl)
    })

    categoryEl.querySelector('[data-menu-action="add-category-fee"]')?.addEventListener("click", () => {
      this.openAddFeeModal(categoryEl)
    })

    const markCategoryUnavailableBtn = categoryEl.querySelector<HTMLElement>('[data-menu-action="mark-category-unavailable"]')
    markCategoryUnavailableBtn?.addEventListener("click", () => {
      this.markUnavailable(categoryEl, categoryEl.querySelector<HTMLElement>(".menu-category-fees"), markCategoryUnavailableBtn)
    })

    const toggleHalfAndHalfBtn = categoryEl.querySelector<HTMLElement>('[data-menu-action="toggle-half-and-half"]')
    toggleHalfAndHalfBtn?.addEventListener("click", () => {
      this.markHalfAndHalf(categoryEl, categoryEl.querySelector<HTMLElement>(".menu-category-fees"), toggleHalfAndHalfBtn)
    })

    categoryEl.querySelector('[data-menu-action="edit-addons"]')?.addEventListener("click", () => {
      this.openAddonsModal(categoryEl)
    })

    addFoodBtn.addEventListener("click", () => this.addFoodItem(categoryEl, categoryId))
  }

  private addFoodItem = (categoryEl: HTMLElement, categoryId: string, initialData?: { name: string; description: string; enabled?: boolean; addons?: any[]; sizes: any[]; fees: any[] }): void => {
    const container = categoryEl.querySelector<HTMLElement>(`[data-food-container="${categoryId}"]`)
    if (!container) return

    const foodId = ++this.foodCounter
    const foodItemEl = document.createElement("div")
    foodItemEl.className = "menu-food-item"
    foodItemEl.dataset.foodId = String(foodId)
    foodItemEl.innerHTML = this.buildFoodHtml(foodId)
    container.appendChild(foodItemEl)

    this.setupFoodItem(foodItemEl)

    if (initialData) {
      const titleEl = foodItemEl.querySelector<HTMLElement>(".menu-food-title")
      if (titleEl) titleEl.textContent = initialData.name

      if (initialData.description) {
        const descriptionEl = foodItemEl.querySelector<HTMLElement>(".menu-food-description")
        const addDescriptionBtn = foodItemEl.querySelector<HTMLElement>('[data-menu-action="add-food-description"]')
        descriptionEl?.classList.remove("d-none")
        if (descriptionEl) descriptionEl.textContent = initialData.description
        addDescriptionBtn?.classList.add("d-none")
      }

      const isSinglePrice = initialData.sizes?.length === 1 && initialData.sizes[0].name === ""
      if (isSinglePrice) {
        const price = initialData.sizes[0].price
        const priceDisplay = foodItemEl.querySelector<HTMLElement>(".menu-price-display")
        const priceInput = foodItemEl.querySelector<HTMLInputElement>(".menu-price-input")
        if (priceDisplay) priceDisplay.textContent = price.toFixed(PanelMenuEditor.PRICE_DECIMAL_PLACES)
        if (priceInput) priceInput.value = String(price)
      } else {
        for (const size of initialData.sizes ?? []) {
          this.addSize(foodItemEl, size)
        }
      }

      const feesContainer = foodItemEl.querySelector<HTMLElement>("[data-fees-container]")
      for (const fee of initialData.fees ?? []) {
        if (feesContainer) this.addFeePill(feesContainer, fee.name, fee.type, fee.price, fee.percentage, fee.deliveryMethods ?? [], fee.sizes ?? [])
      }

      if (initialData.enabled === false) {
        const markBtn = foodItemEl.querySelector<HTMLElement>('[data-menu-action="mark-food-unavailable"]')
        this.markUnavailable(foodItemEl, foodItemEl.querySelector<HTMLElement>(".menu-food-fees"), markBtn)
      }

      if (Array.isArray(initialData.addons) && initialData.addons.length > 0) {
        this.applyAddonsToElement(foodItemEl, initialData.addons)
      }
    } else {
      const title = foodItemEl.querySelector<HTMLElement>(".menu-food-title")
      title?.focus()
    }
  }

  private buildFoodHtml = (foodId: number): string => `
    <div class="menu-food-row">
      <div class="menu-food-left">
        <div class="menu-food-title" contenteditable="true" data-placeholder="${this.t("Item Title")}"></div>
        <div class="menu-food-description d-none" contenteditable="true" data-placeholder="${this.t("Add a description…")}"></div>
        <div class="menu-food-fees" data-fees-container></div>
      </div>
      <div class="menu-food-center">
        <span class="menu-price-display">0.00</span>
        <input type="number" class="menu-price-input form-control form-control-sm d-none" min="0" step="0.01" value="0" />
        <div class="menu-food-sizes d-none">
          <div class="menu-food-sizes-scroll"></div>
        </div>
      </div>
      <div class="dropdown">
        <button type="button" class="btn btn-sm btn-outline-secondary" data-bs-toggle="dropdown" aria-expanded="false">+</button>
        <ul class="dropdown-menu dropdown-menu-end">
          <li><button class="dropdown-item" data-menu-action="add-food-size">${this.t("Size")}</button></li>
          <li><button class="dropdown-item" data-menu-action="add-food-description">${this.t("Description")}</button></li>
          <li><button class="dropdown-item" data-menu-action="add-food-fee">${this.t("Fee")}</button></li>
          <li><button class="dropdown-item" data-menu-action="edit-addons">${this.t("Add-ons")}</button></li>
          <li><button class="dropdown-item" data-menu-action="mark-food-unavailable">${this.t("Mark unavailable")}</button></li>
          <li><hr class="dropdown-divider" /></li>
          <li><button class="dropdown-item text-danger" data-menu-action="delete-food">${this.t("Delete")}</button></li>
        </ul>
      </div>
    </div>
  `

  private setupFoodItem = (foodItemEl: HTMLElement): void => {
    const title = foodItemEl.querySelector<HTMLElement>(".menu-food-title")!
    const priceDisplay = foodItemEl.querySelector<HTMLElement>(".menu-price-display")!
    const priceInput = foodItemEl.querySelector<HTMLInputElement>(".menu-price-input")!
    const description = foodItemEl.querySelector<HTMLElement>(".menu-food-description")!

    title.addEventListener("keydown", (event) => {
      if (event.key === "Enter") { event.preventDefault(); title.blur() }
    })

    priceDisplay.addEventListener("click", () => {
      priceDisplay.classList.add("d-none")
      priceInput.classList.remove("d-none")
      priceInput.focus()
      priceInput.select()
    })

    priceInput.addEventListener("blur", () => {
      priceDisplay.textContent = parseFloat(priceInput.value || PanelMenuEditor.DEFAULT_PRICE).toFixed(PanelMenuEditor.PRICE_DECIMAL_PLACES)
      priceInput.classList.add("d-none")
      priceDisplay.classList.remove("d-none")
    })

    priceInput.addEventListener("keydown", (event) => {
      if (event.key === "Enter") priceInput.blur()
    })

    const addDescriptionBtn = foodItemEl.querySelector<HTMLElement>('[data-menu-action="add-food-description"]')
    if (!description.classList.contains("d-none")) addDescriptionBtn?.classList.add("d-none")
    addDescriptionBtn?.addEventListener("click", () => {
      description.classList.remove("d-none")
      description.focus()
      addDescriptionBtn.classList.add("d-none")
    })

    foodItemEl.querySelector<HTMLElement>('[data-menu-action="add-food-size"]')?.addEventListener("click", () => {
      const scroll = foodItemEl.querySelector<HTMLElement>(".menu-food-sizes-scroll")!
      const isFirstSize = scroll.querySelectorAll(".menu-food-size").length === 0
      this.addSize(foodItemEl)
      if (isFirstSize) {
        const firstSizeEl = scroll.querySelector<HTMLElement>(".menu-food-size")
        const firstSizePriceDisplay = firstSizeEl?.querySelector<HTMLElement>(".menu-food-size-price-display")
        const firstSizePriceInput = firstSizeEl?.querySelector<HTMLInputElement>(".menu-food-size-price-input")
        if (firstSizePriceDisplay) firstSizePriceDisplay.textContent = priceDisplay.textContent
        if (firstSizePriceInput) firstSizePriceInput.value = priceInput.value
        this.addSize(foodItemEl)
      }
    })

    foodItemEl.querySelector('[data-menu-action="add-food-fee"]')?.addEventListener("click", () => {
      this.openAddFeeModal(foodItemEl)
    })

    foodItemEl.querySelector('[data-menu-action="edit-addons"]')?.addEventListener("click", () => {
      this.openAddonsModal(foodItemEl)
    })

    const markFoodUnavailableBtn = foodItemEl.querySelector<HTMLElement>('[data-menu-action="mark-food-unavailable"]')
    markFoodUnavailableBtn?.addEventListener("click", () => {
      this.markUnavailable(foodItemEl, foodItemEl.querySelector<HTMLElement>(".menu-food-fees"), markFoodUnavailableBtn)
    })

    foodItemEl.querySelector('[data-menu-action="delete-food"]')?.addEventListener("click", () => {
      this.confirmDelete(foodItemEl)
    })
  }

  private markUnavailable = (rootEl: HTMLElement, feesContainer: HTMLElement | null, markActionEl: HTMLElement | null): void => {
    if (!feesContainer || rootEl.classList.contains(PanelMenuEditor.CSS_UNAVAILABLE)) return
    rootEl.classList.add(PanelMenuEditor.CSS_UNAVAILABLE)
    markActionEl?.classList.add("d-none")

    const pillEl = document.createElement("span")
    pillEl.className = "menu-unavailable-pill"
    pillEl.dataset.unavailablePill = "true"
    pillEl.innerHTML = `<span>${this.t("Unavailable")}</span><i class="bi bi-x-lg"></i>`
    feesContainer.insertBefore(pillEl, feesContainer.firstChild)

    pillEl.querySelector("i")?.addEventListener("click", () => {
      rootEl.classList.remove(PanelMenuEditor.CSS_UNAVAILABLE)
      markActionEl?.classList.remove("d-none")
      pillEl.remove()
    })
  }

  private markHalfAndHalf = (categoryEl: HTMLElement, feesContainer: HTMLElement | null, toggleActionEl: HTMLElement | null): void => {
    if (!feesContainer || categoryEl.classList.contains(PanelMenuEditor.CSS_HALF_AND_HALF)) return
    categoryEl.classList.add(PanelMenuEditor.CSS_HALF_AND_HALF)
    toggleActionEl?.classList.add("d-none")

    const pillEl = document.createElement("span")
    pillEl.className = "menu-halfhalf-pill"
    pillEl.dataset.halfhalfPill = "true"
    pillEl.innerHTML = `<span>${this.t("Can order half and half")}</span><i class="bi bi-x-lg"></i>`
    feesContainer.insertBefore(pillEl, feesContainer.firstChild)

    pillEl.querySelector("i")?.addEventListener("click", () => {
      categoryEl.classList.remove(PanelMenuEditor.CSS_HALF_AND_HALF)
      toggleActionEl?.classList.remove("d-none")
      pillEl.remove()
    })
  }

  private switchToSizeMode = (foodItemEl: HTMLElement): void => {
    foodItemEl.querySelector<HTMLElement>(".menu-price-display")?.classList.add("d-none")
    foodItemEl.querySelector<HTMLInputElement>(".menu-price-input")?.classList.add("d-none")
    foodItemEl.querySelector<HTMLElement>(".menu-food-sizes")?.classList.remove("d-none")
  }

  private revertToSinglePrice = (foodItemEl: HTMLElement): void => {
    foodItemEl.querySelector<HTMLElement>(".menu-food-sizes")?.classList.add("d-none")
    foodItemEl.querySelector<HTMLElement>(".menu-price-display")?.classList.remove("d-none")
  }

  private addSize = (foodItemEl: HTMLElement, initialData?: { name: string; price: number }): void => {
    const scroll = foodItemEl.querySelector<HTMLElement>(".menu-food-sizes-scroll")!

    const sizeId = ++this.sizeCounter
    const sizeEl = document.createElement("div")
    sizeEl.className = "menu-food-size"
    sizeEl.dataset.sizeId = String(sizeId)
    sizeEl.innerHTML = this.buildSizeHtml()
    scroll.appendChild(sizeEl)

    if (scroll.querySelectorAll(".menu-food-size").length === 1) this.switchToSizeMode(foodItemEl)
    this.setupSize(sizeEl, foodItemEl)

    if (initialData) {
      const titleEl = sizeEl.querySelector<HTMLElement>(".menu-food-size-title")
      const priceDisplay = sizeEl.querySelector<HTMLElement>(".menu-food-size-price-display")
      const priceInput = sizeEl.querySelector<HTMLInputElement>(".menu-food-size-price-input")
      if (titleEl) titleEl.textContent = initialData.name
      if (priceDisplay) priceDisplay.textContent = initialData.price.toFixed(PanelMenuEditor.PRICE_DECIMAL_PLACES)
      if (priceInput) priceInput.value = String(initialData.price)
    } else {
      sizeEl.querySelector<HTMLElement>(".menu-food-size-title")?.focus()
    }
  }

  private buildSizeHtml = (): string => `
    <div class="menu-food-size-header">
      <div class="menu-food-size-title" contenteditable="true" data-placeholder="${this.t("Size")}"></div>
      <i class="menu-food-size-remove bi bi-trash text-danger"></i>
    </div>
    <div class="menu-food-size-price-display">0</div>
    <input type="number" class="menu-food-size-price-input form-control form-control-sm d-none" min="0" step="0.01" value="0" />
  `

  private setupSize = (sizeEl: HTMLElement, foodItemEl: HTMLElement): void => {
    const title = sizeEl.querySelector<HTMLElement>(".menu-food-size-title")!
    const priceDisplay = sizeEl.querySelector<HTMLElement>(".menu-food-size-price-display")!
    const priceInput = sizeEl.querySelector<HTMLInputElement>(".menu-food-size-price-input")!
    const removeBtn = sizeEl.querySelector<HTMLElement>(".menu-food-size-remove")!

    title.addEventListener("keydown", (event) => {
      if (event.key === "Enter") { event.preventDefault(); title.blur() }
    })

    title.addEventListener("input", () => {
      if (title.textContent?.trim()) title.classList.remove("is-invalid")
    })

    priceDisplay.addEventListener("click", () => {
      priceDisplay.classList.add("d-none")
      priceInput.classList.remove("d-none")
      priceInput.focus()
      priceInput.select()
    })

    priceInput.addEventListener("blur", () => {
      priceDisplay.textContent = parseFloat(priceInput.value || PanelMenuEditor.DEFAULT_PRICE).toFixed(PanelMenuEditor.PRICE_DECIMAL_PLACES)
      priceInput.classList.add("d-none")
      priceDisplay.classList.remove("d-none")
    })

    priceInput.addEventListener("keydown", (event) => {
      if (event.key === "Enter") priceInput.blur()
    })

    removeBtn.addEventListener("click", () => {
      const scroll = foodItemEl.querySelector<HTMLElement>(".menu-food-sizes-scroll")!
      sizeEl.remove()
      const remainingSizes = scroll.querySelectorAll<HTMLElement>(".menu-food-size")
      if (remainingSizes.length <= 1) {
        if (remainingSizes.length === 1) {
          const lastPriceDisplay = remainingSizes[0].querySelector<HTMLElement>(".menu-food-size-price-display")
          const lastPriceInput = remainingSizes[0].querySelector<HTMLInputElement>(".menu-food-size-price-input")
          const singlePriceDisplay = foodItemEl.querySelector<HTMLElement>(".menu-price-display")
          const singlePriceInput = foodItemEl.querySelector<HTMLInputElement>(".menu-price-input")
          if (singlePriceDisplay && lastPriceDisplay) singlePriceDisplay.textContent = lastPriceDisplay.textContent
          if (singlePriceInput && lastPriceInput) singlePriceInput.value = lastPriceInput.value
          remainingSizes[0].remove()
        }
        this.revertToSinglePrice(foodItemEl)
      }
    })
  }

  private setupFormSubmit = (): void => {
    const form = document.getElementById("menu-editor-form") as HTMLFormElement
    const menuDataInput = document.getElementById("menu-data-input") as HTMLInputElement
    if (!form || !menuDataInput) return

    form.addEventListener("submit", (event) => {
      if (!this.validateSizeLabels()) {
        event.preventDefault()
        return
      }
      menuDataInput.value = JSON.stringify(this.serializeMenu())

      const submitButtons = form.querySelectorAll<HTMLButtonElement>("button[type=submit]")
      for (const button of submitButtons) {
        button.type = "button"
        button.disabled = true
      }
    })
  }

  private validateSizeLabels = (): boolean => {
    const sizeTitles = document.querySelectorAll<HTMLElement>(".menu-food-size .menu-food-size-title")
    let firstInvalid: HTMLElement | null = null

    for (const title of sizeTitles) {
      const isEmpty = (title.textContent?.trim() ?? "") === ""
      title.classList.toggle("is-invalid", isEmpty)
      if (isEmpty && !firstInvalid) firstInvalid = title
    }

    if (firstInvalid) {
      firstInvalid.scrollIntoView({ block: "center", behavior: "smooth" })
      firstInvalid.focus()
      return false
    }
    return true
  }

  private serializeMenu = () => {
    const categoriesContainer = document.getElementById("menu-categories")
    const intro = document.querySelector<HTMLElement>('[data-menu-note="introduction"] [data-menu-field]')?.innerHTML ?? ""
    const endNotes = document.querySelector<HTMLElement>('[data-menu-note="endnotes"] [data-menu-field]')?.innerHTML ?? ""

    const categories = Array.from(categoriesContainer?.querySelectorAll<HTMLElement>(".menu-category") ?? [])
      .map((categoryEl, categoryIndex) => this.serializeCategory(categoryEl, categoryIndex))

    return { name: "Menu", language: this.languageCode, currency: this.currencyCode, intro, endNotes, categories }
  }

  private serializeCategory = (categoryEl: HTMLElement, categoryIndex: number) => {
    const name = categoryEl.querySelector<HTMLElement>(".menu-category-title")?.textContent?.trim() ?? ""
    const description = categoryEl.querySelector<HTMLElement>(".menu-category-description")?.textContent?.trim() ?? ""

    const fees = Array.from(categoryEl.querySelector(".menu-category-fees")?.querySelectorAll<HTMLElement>(".menu-fee-pill") ?? [])
      .map((feeEl) => this.serializeFee(feeEl))

    const dishes = Array.from(categoryEl.querySelectorAll<HTMLElement>(".menu-food-item"))
      .map((foodEl, dishIndex) => this.serializeDish(foodEl, dishIndex))

    const enabled = !categoryEl.classList.contains(PanelMenuEditor.CSS_UNAVAILABLE)
    const halfAndHalf = categoryEl.classList.contains(PanelMenuEditor.CSS_HALF_AND_HALF)
    const addons = this.parseAddons(categoryEl)

    return { name, description, order: categoryIndex, enabled, halfAndHalf, addons, fees, dishes }
  }

  private serializeDish = (foodEl: HTMLElement, dishIndex: number) => {
    const name = foodEl.querySelector<HTMLElement>(".menu-food-title")?.textContent?.trim() ?? ""
    const description = foodEl.querySelector<HTMLElement>(".menu-food-description")?.textContent?.trim() ?? ""

    const sizeElements = Array.from(foodEl.querySelectorAll<HTMLElement>(".menu-food-size"))
    const sizes = sizeElements.length > 0
      ? sizeElements.map((sizeEl, sizeIndex) => {
          const sizeName = sizeEl.querySelector<HTMLElement>(".menu-food-size-title")?.textContent?.trim() ?? ""
          const sizePrice = parseFloat(sizeEl.querySelector<HTMLInputElement>(".menu-food-size-price-input")?.value || PanelMenuEditor.DEFAULT_PRICE)
          return { name: sizeName, price: sizePrice, order: sizeIndex }
        })
      : [{ name: "", price: parseFloat(foodEl.querySelector<HTMLInputElement>(".menu-price-input")?.value || PanelMenuEditor.DEFAULT_PRICE), order: 0 }]

    const fees = Array.from(foodEl.querySelector(".menu-food-fees")?.querySelectorAll<HTMLElement>(".menu-fee-pill") ?? [])
      .map((feeEl) => {
        const sizeIndexes = (feeEl.dataset.feeSizeIndexes ?? "").split(",").filter(Boolean).map(Number)
        return { ...this.serializeFee(feeEl), sizes: sizeIndexes }
      })

    const enabled = !foodEl.classList.contains(PanelMenuEditor.CSS_UNAVAILABLE)
    const addons = this.parseAddons(foodEl)

    return { name, description, order: dishIndex, enabled, addons, sizes, fees }
  }

  private serializeFee = (feeEl: HTMLElement) => ({
    name: feeEl.dataset.feeName ?? "",
    price: parseFloat(feeEl.dataset.feePrice ?? PanelMenuEditor.DEFAULT_PRICE),
    percentage: parseFloat(feeEl.dataset.feePercentage ?? PanelMenuEditor.DEFAULT_PRICE),
    type: (feeEl.dataset.feeType ?? DishFeeType.Fixed) as DishFeeType,
    deliveryMethods: (feeEl.dataset.feeDeliveryMethods ?? "").split(",").filter(Boolean),
  })

  private setupFeeModal = (): void => {
    const modalEl = document.getElementById("menu-fee-modal")
    if (!modalEl) return
    this.feeModal = new Modal(modalEl)

    const typeSelect = document.getElementById("menu-fee-type") as HTMLSelectElement
    const priceGroup = document.getElementById("menu-fee-price-group")!
    const percentageGroup = document.getElementById("menu-fee-percentage-group")!

    typeSelect?.addEventListener("change", () => {
      if (typeSelect.value === DishFeeType.Fixed) {
        priceGroup.classList.remove("d-none")
        percentageGroup.classList.add("d-none")
      } else {
        priceGroup.classList.add("d-none")
        percentageGroup.classList.remove("d-none")
      }
    })

    document.getElementById("menu-fee-confirm-btn")?.addEventListener("click", () => {
      const nameInput = document.getElementById("menu-fee-name") as HTMLInputElement
      const name = nameInput.value.trim()
      if (!name) { nameInput.focus(); return }

      const type = typeSelect.value as DishFeeType
      const price = parseFloat((document.getElementById("menu-fee-price") as HTMLInputElement).value || "0")
      const percentage = parseFloat((document.getElementById("menu-fee-percentage") as HTMLInputElement).value || "0")
      const deliveryMethods = Array.from(
        document.querySelectorAll<HTMLInputElement>(".menu-fee-method-checkbox:checked")
      ).map((checkbox) => checkbox.value)
      const sizeIndexes = Array.from(
        document.querySelectorAll<HTMLInputElement>(".menu-fee-size-checkbox:checked")
      ).map((checkbox) => parseInt(checkbox.value, 10))

      if (this.pendingFeeEditEl) {
        this.pendingFeeEditEl.dataset.feeName = name
        this.pendingFeeEditEl.dataset.feeType = type
        this.pendingFeeEditEl.dataset.feePrice = String(price)
        this.pendingFeeEditEl.dataset.feePercentage = String(percentage)
        this.pendingFeeEditEl.dataset.feeDeliveryMethods = deliveryMethods.join(",")
        this.pendingFeeEditEl.dataset.feeSizeIndexes = sizeIndexes.join(",")
        const labelEl = this.pendingFeeEditEl.querySelector<HTMLElement>(".menu-fee-label")
        if (labelEl) labelEl.textContent = this.buildFeeLabelText(name, type, price, percentage)
      } else if (this.pendingFeeParentEl) {
        const feesContainer = this.pendingFeeParentEl.querySelector<HTMLElement>("[data-fees-container]")
        if (!feesContainer) return
        this.addFeePill(feesContainer, name, type, price, percentage, deliveryMethods, sizeIndexes)
      }

      this.pendingFeeParentEl = null
      this.pendingFeeEditEl = null
      this.feeModal?.hide()
    })
  }

  private openAddFeeModal = (parentEl: HTMLElement): void => {
    this.pendingFeeParentEl = parentEl
    this.pendingFeeEditEl = null

    ;(document.getElementById("menu-fee-name") as HTMLInputElement).value = ""
    const typeSelect = document.getElementById("menu-fee-type") as HTMLSelectElement
    typeSelect.value = DishFeeType.Fixed
    ;(document.getElementById("menu-fee-price") as HTMLInputElement).value = "0"
    ;(document.getElementById("menu-fee-percentage") as HTMLInputElement).value = "0"
    document.querySelectorAll<HTMLInputElement>(".menu-fee-method-checkbox").forEach((checkbox) => { checkbox.checked = false })
    document.getElementById("menu-fee-price-group")?.classList.remove("d-none")
    document.getElementById("menu-fee-percentage-group")?.classList.add("d-none")

    const foodItemEl = parentEl.classList.contains("menu-food-item") ? parentEl : null
    const sizeLabels = foodItemEl ? this.getSizeLabels(foodItemEl) : []
    const sizesGroup = document.getElementById("menu-fee-sizes-group")!
    if (foodItemEl && sizeLabels.length >= PanelMenuEditor.MIN_SIZES_FOR_FEE_SELECTION) {
      this.populateSizesCheckboxes(sizeLabels, [])
      sizesGroup.classList.remove("d-none")
    } else {
      sizesGroup.classList.add("d-none")
    }

    const modalTitle = document.getElementById("menu-fee-modal-title")
    if (modalTitle) modalTitle.textContent = this.t("Add fee")
    const confirmBtn = document.getElementById("menu-fee-confirm-btn")
    if (confirmBtn) confirmBtn.textContent = this.t("Add")

    this.feeModal?.show()
  }

  private openEditFeeModal = (feeEl: HTMLElement): void => {
    this.pendingFeeEditEl = feeEl
    this.pendingFeeParentEl = null

    const type = (feeEl.dataset.feeType ?? DishFeeType.Fixed) as DishFeeType
    const deliveryMethods = (feeEl.dataset.feeDeliveryMethods ?? "").split(",").filter(Boolean)
    const selectedSizeIndexes = (feeEl.dataset.feeSizeIndexes ?? "").split(",").filter(Boolean).map(Number)

    ;(document.getElementById("menu-fee-name") as HTMLInputElement).value = feeEl.dataset.feeName ?? ""
    const typeSelect = document.getElementById("menu-fee-type") as HTMLSelectElement
    typeSelect.value = type
    ;(document.getElementById("menu-fee-price") as HTMLInputElement).value = feeEl.dataset.feePrice ?? "0"
    ;(document.getElementById("menu-fee-percentage") as HTMLInputElement).value = feeEl.dataset.feePercentage ?? "0"

    if (type === DishFeeType.Fixed) {
      document.getElementById("menu-fee-price-group")?.classList.remove("d-none")
      document.getElementById("menu-fee-percentage-group")?.classList.add("d-none")
    } else {
      document.getElementById("menu-fee-price-group")?.classList.add("d-none")
      document.getElementById("menu-fee-percentage-group")?.classList.remove("d-none")
    }

    document.querySelectorAll<HTMLInputElement>(".menu-fee-method-checkbox").forEach((checkbox) => {
      checkbox.checked = deliveryMethods.includes(checkbox.value)
    })

    const foodItemEl = feeEl.closest<HTMLElement>(".menu-food-item")
    const sizeLabels = foodItemEl ? this.getSizeLabels(foodItemEl) : []
    const sizesGroup = document.getElementById("menu-fee-sizes-group")!
    if (foodItemEl && sizeLabels.length >= PanelMenuEditor.MIN_SIZES_FOR_FEE_SELECTION) {
      this.populateSizesCheckboxes(sizeLabels, selectedSizeIndexes)
      sizesGroup.classList.remove("d-none")
    } else {
      sizesGroup.classList.add("d-none")
    }

    const modalTitle = document.getElementById("menu-fee-modal-title")
    if (modalTitle) modalTitle.textContent = this.t("Edit fee")
    const confirmBtn = document.getElementById("menu-fee-confirm-btn")
    if (confirmBtn) confirmBtn.textContent = this.t("Save")

    this.feeModal?.show()
  }

  private getSizeLabels = (foodItemEl: HTMLElement): string[] =>
    Array.from(foodItemEl.querySelectorAll<HTMLElement>(".menu-food-size-title"))
      .map((titleEl, index) => titleEl.textContent?.trim() || `${this.t("Size")} ${index + 1}`)

  private populateSizesCheckboxes = (labels: string[], selectedIndexes: number[]): void => {
    const container = document.getElementById("menu-fee-sizes-checkboxes")!
    container.innerHTML = labels.map((label, index) => `
      <div class="form-check">
        <input class="form-check-input menu-fee-size-checkbox" type="checkbox"
               id="menu-fee-size-${index}" value="${index}" ${selectedIndexes.includes(index) ? "checked" : ""} />
        <label class="form-check-label" for="menu-fee-size-${index}">${label}</label>
      </div>
    `).join("")
  }

  private buildFeePillHtml = (label: string): string => `
    <span class="menu-fee-label">${label}</span>
    <i class="menu-fee-edit bi bi-pencil-fill"></i>
  `

  private setupFeePill = (feeEl: HTMLElement): void => {
    feeEl.querySelector<HTMLElement>(".menu-fee-edit")?.addEventListener("click", () => {
      this.openEditFeeModal(feeEl)
    })
  }

  private buildFeeLabelText = (name: string, type: DishFeeType, price: number, percentage: number): string =>
    type === DishFeeType.Fixed
      ? `${name} ${Currency.showPriceAsText(Money.toSafe(price), this.currencyCode)}`
      : `${name} ${percentage}%`

  private setupAddonsModal = (): void => {
    const modalEl = document.getElementById("menu-addons-modal")
    if (!modalEl) return
    this.addonsModal = new Modal(modalEl)

    document.getElementById("menu-addon-add-row")?.addEventListener("click", () => this.addAddonRow())

    document.getElementById("menu-addons-save-btn")?.addEventListener("click", () => {
      if (this.pendingAddonsEl) {
        this.applyAddonsToElement(this.pendingAddonsEl, this.collectAddonsFromModal())
      }
      this.pendingAddonsEl = null
      this.addonsModal?.hide()
    })
  }

  private openAddonsModal = (targetEl: HTMLElement): void => {
    this.pendingAddonsEl = targetEl

    const rowsContainer = document.getElementById("menu-addons-rows")
    if (rowsContainer) rowsContainer.innerHTML = ""
    for (const addon of this.parseAddons(targetEl)) this.addAddonRow(addon)
    this.updateAddonsEmptyHint()

    this.addonsModal?.show()
  }

  private buildAddonRowHtml = (): string => `
    <div class="row g-2 align-items-end">
      <div class="col-12 col-sm">
        <label class="form-label small mb-1">${this.t("Name")}</label>
        <input type="text" class="form-control form-control-sm" data-addon-name />
      </div>
      <div class="col-6 col-sm-3">
        <label class="form-label small mb-1">${this.t("Price")}</label>
        <input type="number" min="0" step="0.01" class="form-control form-control-sm" data-addon-price value="0" />
      </div>
      <div class="col-auto">
        <button type="button" class="btn btn-sm btn-outline-danger" data-addon-remove title="${this.t("Remove")}"><i class="bi bi-trash3"></i></button>
      </div>
    </div>
  `

  private addAddonRow = (addon?: { name: string; price: number }): void => {
    const rowsContainer = document.getElementById("menu-addons-rows")
    if (!rowsContainer) return

    const rowEl = document.createElement("div")
    rowEl.className = "menu-addon-row border rounded p-2"
    rowEl.dataset.addonRow = "true"
    rowEl.innerHTML = this.buildAddonRowHtml()
    rowsContainer.appendChild(rowEl)

    if (addon) {
      rowEl.querySelector<HTMLInputElement>("[data-addon-name]")!.value = addon.name
      rowEl.querySelector<HTMLInputElement>("[data-addon-price]")!.value = String(addon.price)
    }

    rowEl.querySelector("[data-addon-remove]")?.addEventListener("click", () => {
      rowEl.remove()
      this.updateAddonsEmptyHint()
    })

    this.updateAddonsEmptyHint()
  }

  private collectAddonsFromModal = (): { name: string; price: number }[] => {
    const rows = Array.from(document.querySelectorAll<HTMLElement>("#menu-addons-rows [data-addon-row]"))
    const addons: { name: string; price: number }[] = []
    for (const rowEl of rows) {
      const name = rowEl.querySelector<HTMLInputElement>("[data-addon-name]")?.value.trim() ?? ""
      if (!name) continue
      const price = Math.max(0, parseFloat(rowEl.querySelector<HTMLInputElement>("[data-addon-price]")?.value || "0") || 0)
      addons.push({ name, price })
    }
    return addons
  }

  private updateAddonsEmptyHint = (): void => {
    const rowsContainer = document.getElementById("menu-addons-rows")
    const hasRows = (rowsContainer?.querySelectorAll("[data-addon-row]").length ?? 0) > 0
    document.getElementById("menu-addons-empty")?.classList.toggle("d-none", hasRows)
  }

  private parseAddons = (targetEl: HTMLElement): { name: string; price: number }[] => {
    try {
      const parsed = JSON.parse(targetEl.dataset.addons ?? "[]")
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }

  private applyAddonsToElement = (targetEl: HTMLElement, addons: { name: string; price: number }[]): void => {
    const feesContainer = targetEl.classList.contains("menu-food-item")
      ? targetEl.querySelector<HTMLElement>(".menu-food-fees")
      : targetEl.querySelector<HTMLElement>(".menu-category-fees")

    feesContainer?.querySelector("[data-addons-pill]")?.remove()

    if (addons.length === 0) {
      delete targetEl.dataset.addons
      return
    }

    targetEl.dataset.addons = JSON.stringify(addons)
    if (!feesContainer) return

    const pillEl = document.createElement("span")
    pillEl.className = "menu-addons-pill"
    pillEl.dataset.addonsPill = "true"
    pillEl.innerHTML = `<span>${this.t("Add-ons")}</span><i class="menu-fee-edit bi bi-pencil-fill"></i>`
    feesContainer.appendChild(pillEl)

    pillEl.querySelector<HTMLElement>(".menu-fee-edit")?.addEventListener("click", () => this.openAddonsModal(targetEl))
  }

  private addFeePill = (
    feesContainer: HTMLElement,
    name: string,
    type: DishFeeType,
    price: number,
    percentage: number,
    deliveryMethods: string[],
    sizeIndexes: number[]
  ): void => {
    const feeEl = document.createElement("span")
    feeEl.className = "menu-fee-pill"
    feeEl.dataset.feeId = String(++this.feeCounter)
    feeEl.dataset.feeName = name
    feeEl.dataset.feeType = type
    feeEl.dataset.feePrice = String(price)
    feeEl.dataset.feePercentage = String(percentage)
    feeEl.dataset.feeDeliveryMethods = deliveryMethods.join(",")
    feeEl.dataset.feeSizeIndexes = sizeIndexes.join(",")
    feeEl.innerHTML = this.buildFeePillHtml(this.buildFeeLabelText(name, type, price, percentage))
    feesContainer.appendChild(feeEl)
    this.setupFeePill(feeEl)
  }

  private initOcrState = (): void => {
    const photosPanel = document.querySelector<HTMLElement>('[data-menu-panel="photos"]')
    if (!photosPanel) return

    const isOcrRunning = photosPanel.dataset.ocrRunning === "true"
    const menuOcrError = photosPanel.dataset.menuOcrError ?? ""

    if (isOcrRunning) {
      this.isCreatingMenu = true
      this.setDropzoneLoading(true)
      this.currentMode = PanelMenuEditor.MENU_MODE_PHOTOS
      const toggleBtn = document.querySelector<HTMLButtonElement>("[data-menu-mode-toggle]")
      if (toggleBtn) toggleBtn.disabled = true
      document.querySelectorAll<HTMLElement>("[data-menu-panel]").forEach((panel) => {
        panel.style.display = panel.dataset.menuPanel === PanelMenuEditor.MENU_MODE_PHOTOS ? "" : "none"
      })
      const createBtn = document.querySelector<HTMLButtonElement>("[data-menu-create-btn]")
      if (createBtn) {
        createBtn.disabled = true
        createBtn.innerHTML = `<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>${this.t("Processing...")}`
      }
      this.startOcrCountdown(photosPanel)
    } else if (menuOcrError) {
      const errorEl = document.querySelector<HTMLElement>("[data-menu-create-error]")
      if (errorEl) {
        errorEl.textContent = this.t("Menu generation failed. Please try again.")
        errorEl.classList.remove("d-none")
      }
    }
  }

  private startOcrCountdown = (photosPanel: HTMLElement): void => {
    const alertEl = document.querySelector<HTMLElement>("[data-menu-generation-alert]")
    const remainingSeconds = Number(photosPanel.dataset.ocrRemainingSeconds)
    if (!alertEl || !Number.isFinite(remainingSeconds) || remainingSeconds <= 0) return

    const deadline = Date.now() + remainingSeconds * PanelMenuEditor.MS_IN_SECOND
    const updateAlert = (): void => {
      const secondsLeft = Math.max(0, Math.ceil((deadline - Date.now()) / PanelMenuEditor.MS_IN_SECOND))
      alertEl.textContent = this.t("Menu generation is in progress. It should be over in {seconds} seconds.").replace("{seconds}", String(secondsLeft))
      if (secondsLeft <= 0) {
        this.stopOcrCountdown()
        this.pjax.loadUrl(window.location.href)
      }
    }
    alertEl.classList.remove("d-none")
    updateAlert()
    this.ocrCountdownTimer = window.setInterval(updateAlert, PanelMenuEditor.MS_IN_SECOND)
  }

  private stopOcrCountdown = (): void => {
    if (this.ocrCountdownTimer !== null) {
      window.clearInterval(this.ocrCountdownTimer)
      this.ocrCountdownTimer = null
    }
    document.querySelector<HTMLElement>("[data-menu-generation-alert]")?.classList.add("d-none")
  }

  private preRenderMenu = (): void => {
    const scriptEl = document.getElementById("menu-data")
    if (!scriptEl) return

    let menu: any
    try {
      menu = JSON.parse(scriptEl.textContent ?? "")
    } catch {
      return
    }

    const introEl = document.querySelector<HTMLElement>('[data-menu-note="introduction"] [data-menu-field]')
    if (introEl && menu.intro) introEl.innerHTML = menu.intro

    const endNotesEl = document.querySelector<HTMLElement>('[data-menu-note="endnotes"] [data-menu-field]')
    if (endNotesEl && menu.endNotes) endNotesEl.innerHTML = menu.endNotes

    for (const category of menu.categories ?? []) {
      this.addCategory(category)
    }

    if (this.wasMenuCreated) {
      this.currentMode = PanelMenuEditor.MENU_MODE_MANUAL
      document.querySelectorAll<HTMLElement>("[data-menu-panel]").forEach((panel) => {
        panel.style.display = panel.dataset.menuPanel === PanelMenuEditor.MENU_MODE_MANUAL ? "" : "none"
      })
      this.updateHeaderText()
    }
  }

  private onMenuGenerationStatus = (data: MenuGenerationStatus): void => {
    if (data.success) {
      window.location.reload()
      return
    }

    this.isCreatingMenu = false
    this.selectedFiles = []
    this.setDropzoneLoading(false)
    this.stopOcrCountdown()

    const createBtn = document.querySelector<HTMLButtonElement>("[data-menu-create-btn]")
    const toggleBtn = document.querySelector<HTMLButtonElement>("[data-menu-mode-toggle]")
    if (createBtn) {
      createBtn.disabled = true
      createBtn.innerHTML = this.createBtnOriginalHTML
    }
    if (toggleBtn) toggleBtn.disabled = false

    const errorEl = document.querySelector<HTMLElement>("[data-menu-create-error]")
    if (errorEl) {
      errorEl.textContent = this.t("Menu generation failed. Please try again.")
      errorEl.classList.remove("d-none")
    }
  }

  onPageClose = (): void => {
    SocketClient.off(SocketEvents.MenuGenerationStatus, this.onMenuGenerationStatus)
    this.stopOcrCountdown()
  }
}
